import React, { useContext, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faCalendarDay,
  faCircleCheck,
  faComments,
  faCoins,
  faHashtag,
  faListCheck
} from '@fortawesome/free-solid-svg-icons';
import NavigationButtons from '../NavigationButtons';
import { SelectedOfferContext } from '../../../context/SelectedOfferContext';
import { submitRequestForAnalysis } from '../../../services/comersRequestFlow';
import { isRequestBlocked } from '../../../utils/requestStatusGuard';
import { formatDate as formatShortDate } from '../../../utils/dateFormatter';
import './PreConfirmationStep.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);

const formatDate = (value) => {
  if (!value) {
    return 'N/A';
  }

  return formatShortDate(value) || 'N/A';
};

const getFallbackFirstDueDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date;
};

const ANALYST_COMMENT_SUGGESTIONS = [
  'Cliente validó verbalmente los datos cargados.',
  'La documentación fue revisada con el cliente antes del envío.',
  'El cliente aclaró diferencias en ingresos y egresos.',
  'Solicitar revisión prioritaria si la documentación está correcta.'
];

const PreConfirmationStep = ({ onBack, onNext, onViewRequests, requestId, demoMode = false }) => {
  const { selectedOffer } = useContext(SelectedOfferContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submittedStatusLabel, setSubmittedStatusLabel] = useState(null);
  const [sellerComment, setSellerComment] = useState('');
  const firstDueDate = useMemo(
    () => selectedOffer?.firstDueDate || getFallbackFirstDueDate(),
    [selectedOffer?.firstDueDate]
  );
  const analysisSubmitted = submittedStatusLabel !== null;

  const summaryCards = useMemo(() => ([
    {
      id: 'amount',
      label: 'Monto solicitado',
      value: formatCurrency(selectedOffer?.amount),
      icon: faCoins
    },
    {
      id: 'installments',
      label: 'Cantidad de cuotas',
      value: selectedOffer?.term || 0,
      icon: faHashtag
    },
    {
      id: 'installmentValue',
      label: 'Valor por cuota',
      value: formatCurrency(selectedOffer?.monthlyPayment),
      icon: faArrowTrendUp
    },
    {
      id: 'firstDueDate',
      label: 'Primer vencimiento',
      value: formatDate(firstDueDate),
      icon: faCalendarDay
    }
  ]), [firstDueDate, selectedOffer?.amount, selectedOffer?.monthlyPayment, selectedOffer?.term]);

  const appendSuggestedComment = (text) => {
    setSellerComment((current) => {
      const normalized = current.trim();
      if (!normalized) {
        return text;
      }

      if (normalized.includes(text)) {
        return current;
      }

      return `${normalized}\n${text}`;
    });
  };

  const handleFinish = async () => {
    if (analysisSubmitted) {
      return;
    }

    if (!demoMode && !requestId) {
      setSubmitError('No se encontró la solicitud a enviar a análisis.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let statusLabel = 'Análisis de riesgo';

      if (!demoMode) {
        const response = await submitRequestForAnalysis({
          requestUid: requestId,
          comment: sellerComment.trim()
        });

        if (isRequestBlocked(response?.requestStatus)) {
          return;
        }

        statusLabel = response?.requestStatus?.statusDetail?.message || response?.data?.statusLabel || statusLabel;
      }

      setSubmittedStatusLabel(statusLabel);

      onNext(
        {
          preConfirmation: {
            status: 'PRE_APPROVED',
            statusLabel,
            reviewedBy: 'Analista de Riesgo',
            submittedAt: new Date().toISOString()
          }
        },
        'success'
      );
    } catch (error) {
      console.error('Error sending request to analysis:', error);

      if (isRequestBlocked(error?.response?.data?.requestStatus)) {
        return;
      }

      setSubmitError(
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'No se pudo enviar la solicitud a análisis.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (analysisSubmitted) {
    return (
      <div className="container rg-request-confirmation-step rg-request-preconfirmation-step">
        <section className="rg-request-preconfirmation-step__panel rg-request-preconfirmation-step__success-panel">
          <span className="rg-request-preconfirmation-step__success-icon">
            <FontAwesomeIcon icon={faCircleCheck} />
          </span>
          <div>
            <h4 className="rg-request-preconfirmation-step__success-title">
              Solicitud enviada al analista
            </h4>
            <p className="rg-request-preconfirmation-step__success-message">
              La solicitud quedó en estado {submittedStatusLabel || 'En análisis'} y el equipo de riesgo ya puede revisarla.
            </p>
          </div>
        </section>

        <section className="rg-request-preconfirmation-step__panel rg-request-preconfirmation-step__next-steps-panel">
          <h5 className="rg-request-section__title">
            <FontAwesomeIcon icon={faListCheck} />
            Próximos pasos
          </h5>

          <ol className="rg-request-preconfirmation-step__next-steps">
            <li>
              <div className="rg-request-preconfirmation-step__next-step-copy">
                <strong>Revisión de documentación</strong>
                <span>El analista valida archivos, datos cargados y consistencia de la solicitud.</span>
              </div>
            </li>
            <li>
              <div className="rg-request-preconfirmation-step__next-step-copy">
                <strong>Decisión del caso</strong>
                <span>La solicitud puede avanzar, requerir una corrección o quedar observada.</span>
              </div>
            </li>
            <li>
              <div className="rg-request-preconfirmation-step__next-step-copy">
                <strong>Continuidad del flujo</strong>
                <span>Si se aprueba, continuará con oferta final, firma y formalización.</span>
              </div>
            </li>
          </ol>
        </section>

        <NavigationButtons
          onNext={typeof onViewRequests === 'function' ? onViewRequests : null}
          nextLabel="Ver solicitudes"
        />
      </div>
    );
  }

  return (
    <div className="container rg-request-confirmation-step rg-request-preconfirmation-step">
      <div className="rg-request-preconfirmation-step__layout">
        <section className="rg-request-preconfirmation-step__panel rg-request-preconfirmation-step__panel--summary">
          <div className="rg-request-preconfirmation-step__summary-list">
            {summaryCards.map((item) => (
              <article key={item.id} className="rg-request-preconfirmation-step__summary-row">
                <span className="rg-request-preconfirmation-step__metric-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <div className="rg-request-preconfirmation-step__summary-copy">
                  <span className="rg-request-preconfirmation-step__metric-label">{item.label}</span>
                  <strong className="rg-request-preconfirmation-step__metric-value">{item.value}</strong>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>

      {submitError && (
        <div className="alert alert-warning border mb-0">
          <strong className="d-block mb-1">No se pudo enviar a análisis</strong>
          <small>{submitError}</small>
        </div>
      )}

      <section className="rg-request-preconfirmation-step__panel rg-request-preconfirmation-step__panel--comment">
        <div className="rg-request-preconfirmation-step__panel-header">
          <div>
            <h5 className="rg-request-section__title">
              <FontAwesomeIcon icon={faComments} />
              Comentario para el analista
            </h5>
          </div>
          <span className="rg-request-preconfirmation-step__optional-badge">Opcional</span>
        </div>

        <div className="rg-request-preconfirmation-step__suggestions">
          {ANALYST_COMMENT_SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="rg-request-preconfirmation-step__suggestion-chip"
              onClick={() => appendSuggestedComment(suggestion)}
              disabled={analysisSubmitted || isSubmitting}
            >
              {suggestion}
            </button>
          ))}
        </div>

        <textarea
          className="form-control rg-request-preconfirmation-step__textarea"
          rows="5"
          maxLength="1000"
          value={sellerComment}
          onChange={(event) => setSellerComment(event.target.value)}
          disabled={analysisSubmitted || isSubmitting}
        />

        <div className="rg-request-preconfirmation-step__comment-footer">
          <span className="rg-request-preconfirmation-step__char-count">{sellerComment.length}/1000</span>
        </div>
      </section>

      <NavigationButtons
        onBack={analysisSubmitted ? null : onBack}
        onNext={analysisSubmitted ? null : handleFinish}
        nextLabel={analysisSubmitted ? 'En análisis' : 'Enviar a análisis'}
        isLoading={isSubmitting}
        disableNext={analysisSubmitted}
      />
    </div>
  );
};

export default PreConfirmationStep;
